import React, { useState } from 'react';
import {
  Modal,
  Progress,
  Space,
  Typography,
  Button,
  List,
  Alert,
  Statistic,
  Collapse,
  Tag,
  Divider,
  Tooltip
} from 'antd';
import {
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';

const { Text, Title } = Typography;

/**
 * Enhanced progress modal component for sequential campaign save operations
 * Shows real-time progress, individual line item status, and advanced controls
 */
export const CampaignSaveProgress = ({
  progress,
  onCancel,
  onRetry,
  onPause,
  onResume,
  campaignOrderId,
  parentCampaignId
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [retryInProgress, setRetryInProgress] = useState(false);
  const getStatusIcon = () => {
    if (progress.isPaused) return <PauseCircleOutlined style={{ color: '#faad14' }} />;
    if (progress.failedLineItems?.length > 0 && !progress.isActive) {
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    }
    if (progress.percent === 100) return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    if (progress.isActive) return <LoadingOutlined spin style={{ color: '#1890ff' }} />;
    return <ClockCircleOutlined style={{ color: '#8c8c8c' }} />;
  };

  const getLineItemStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'failed':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'processing':
        return <LoadingOutlined spin style={{ color: '#1890ff' }} />;
      default:
        return <ClockCircleOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  const getLineItemStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'error';
      case 'processing': return 'processing';
      default: return 'default';
    }
  };

  const getProgressStatus = () => {
    if (progress.failedLineItems?.length > 0 && !progress.isActive) return 'exception';
    if (progress.percent === 100 && progress.failedLineItems?.length === 0) return 'success';
    if (progress.isPaused) return 'normal';
    return 'active';
  };

  const handleRetryFailed = async () => {
    if (retryInProgress || !onRetry) return;

    setRetryInProgress(true);
    try {
      await onRetry(campaignOrderId, parentCampaignId);
    } finally {
      setRetryInProgress(false);
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <Modal
      open={progress.isActive}
      title={
        <Space>
          {getStatusIcon()}
          <span>Saving Campaign</span>
        </Space>
      }
      footer={[
        <Button
          key="cancel"
          onClick={onCancel}
          danger
          disabled={progress.percent === 100 || progress.phase === 'Complete'}
        >
          {progress.percent === 100 ? 'Close' : 'Cancel'}
        </Button>,
        progress.isPaused && onResume && (
          <Button
            key="resume"
            onClick={onResume}
            type="primary"
            icon={<PlayCircleOutlined />}
          >
            Resume
          </Button>
        ),
        !progress.isPaused && progress.isActive && onPause && (
          <Button
            key="pause"
            onClick={onPause}
            icon={<PauseCircleOutlined />}
          >
            Pause
          </Button>
        ),
        progress.canRetry && !progress.isActive && (
          <Button
            key="retry"
            onClick={handleRetryFailed}
            type="primary"
            icon={<ReloadOutlined />}
            loading={retryInProgress}
          >
            Retry Failed Items ({progress.failedLineItems?.length || 0})
          </Button>
        ),
      ]}
      closable={false}
      maskClosable={false}
      width={600}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Current Phase */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase' }}>
                Current Phase
              </Text>
              <Title level={4} style={{ margin: 0 }}>
                {progress.phase}
                {progress.isPaused && <Tag color="warning" style={{ marginLeft: 8 }}>PAUSED</Tag>}
              </Title>
            </div>
            {progress.lineItemStatus?.length > 0 && (
              <Button
                type="link"
                size="small"
                onClick={() => setShowDetails(!showDetails)}
                icon={showDetails ? <UpOutlined /> : <DownOutlined />}
              >
                {showDetails ? 'Hide' : 'Show'} Details
              </Button>
            )}
          </div>
          {progress.currentOperation && (
            <Text style={{ fontSize: 14, color: '#8c8c8c' }}>{progress.currentOperation}</Text>
          )}
          {progress.currentLineItem && (
            <div style={{ marginTop: 8, padding: 8, background: '#f0f2f5', borderRadius: 4 }}>
              <Text strong style={{ fontSize: 13 }}>
                Currently processing: {progress.currentLineItem.name}
              </Text>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <Progress
          percent={progress.percent}
          status={getProgressStatus()}
          strokeColor={{
            '0%': '#108ee9',
            '100%': '#87d068',
          }}
          strokeWidth={12}
        />

        {/* Statistics */}
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <Statistic
            title="Progress"
            value={progress.current}
            suffix={`/ ${progress.total}`}
            valueStyle={{ fontSize: 18 }}
          />
          {progress.completedLineItems?.length > 0 && (
            <Statistic
              title="Completed"
              value={progress.completedLineItems.length}
              valueStyle={{ fontSize: 18, color: '#52c41a' }}
            />
          )}
          {progress.failedLineItems?.length > 0 && (
            <Statistic
              title="Failed"
              value={progress.failedLineItems.length}
              valueStyle={{ fontSize: 18, color: '#ff4d4f' }}
            />
          )}
          {progress.estimatedTime > 0 && progress.percent < 100 && !progress.isPaused && (
            <Statistic
              title="Est. Time"
              value={formatTime(progress.estimatedTime)}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ fontSize: 18 }}
            />
          )}
          <Statistic
            title="Completion"
            value={progress.percent}
            suffix="%"
            valueStyle={{
              fontSize: 18,
              color: getProgressStatus() === 'exception' ? '#ff4d4f' :
                     getProgressStatus() === 'success' ? '#52c41a' : undefined
            }}
          />
        </div>

        {/* Line Item Details */}
        {showDetails && progress.lineItemStatus?.length > 0 && (
          <>
            <Divider orientation="left" style={{ margin: '16px 0 8px 0' }}>
              Line Item Status
            </Divider>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <List
                size="small"
                dataSource={progress.lineItemStatus}
                renderItem={(item, index) => (
                  <List.Item
                    style={{
                      padding: '8px 12px',
                      backgroundColor: item.status === 'processing' ? '#e6f7ff' : 'transparent',
                      borderRadius: 4,
                      marginBottom: 4,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <div style={{ marginRight: 8 }}>
                        {getLineItemStatusIcon(item.status)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ fontSize: 13 }}>
                          {index + 1}. {item.name}
                        </Text>
                        {item.error && (
                          <div style={{ marginTop: 2 }}>
                            <Text type="danger" style={{ fontSize: 12 }}>
                              Error: {item.error}
                            </Text>
                          </div>
                        )}
                      </div>
                      <div style={{ marginLeft: 8 }}>
                        <Tag color={getLineItemStatusColor(item.status)} size="small">
                          {item.status.toUpperCase()}
                        </Tag>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            </div>
          </>
        )}

        {/* Error Summary */}
        {progress.failedLineItems?.length > 0 && !progress.isActive && (
          <Alert
            message={`${progress.failedLineItems.length} line item${progress.failedLineItems.length === 1 ? '' : 's'} failed to save`}
            description={
              <div>
                <Text style={{ fontSize: 13 }}>
                  Campaign order was created successfully, but some line items encountered errors.
                  You can retry the failed items or continue with the campaign as-is.
                </Text>
                {!showDetails && (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      type="link"
                      size="small"
                      onClick={() => setShowDetails(true)}
                    >
                      View failed line items
                    </Button>
                  </div>
                )}
              </div>
            }
            type="warning"
            showIcon
          />
        )}

        {/* General Errors */}
        {progress.errors?.length > 0 && (
          <Alert
            message="Campaign save encountered errors"
            description={
              <List
                size="small"
                dataSource={progress.errors}
                renderItem={(error, index) => (
                  <List.Item style={{ padding: '4px 0' }}>
                    <Text type="danger" style={{ fontSize: 13 }}>
                      {index + 1}. {error}
                    </Text>
                  </List.Item>
                )}
                style={{ maxHeight: 150, overflowY: 'auto' }}
              />
            }
            type="error"
            showIcon
          />
        )}

        {/* Success Message */}
        {progress.percent === 100 && progress.failedLineItems?.length === 0 && (
          <Alert
            message="Campaign saved successfully!"
            description={`Campaign order and all ${progress.completedLineItems?.length || 0} line items have been processed and saved.`}
            type="success"
            showIcon
          />
        )}

        {/* Partial Success Message */}
        {progress.percent === 100 && progress.failedLineItems?.length > 0 && (
          <Alert
            message="Campaign partially saved"
            description={
              <div>
                <Text>
                  Campaign order created with {progress.completedLineItems?.length || 0} successful line items.
                </Text>
                <br />
                <Text strong>
                  {progress.failedLineItems.length} line item{progress.failedLineItems.length === 1 ? '' : 's'} failed and can be retried.
                </Text>
              </div>
            }
            type="warning"
            showIcon
          />
        )}

        {/* Tips and Information */}
        {progress.isActive && progress.percent < 100 && (
          <div style={{
            padding: 8,
            background: '#e6f7ff',
            borderRadius: 4,
            border: '1px solid #91d5ff'
          }}>
            <Text style={{ fontSize: 12, color: '#0050b3' }}>
              💡 Each line item is saved individually for better reliability.
              {progress.isPaused ? 'Operation is paused - click Resume to continue.' :
               'You can pause the operation at any time.'}
            </Text>
          </div>
        )}

        {/* Retry Information */}
        {progress.canRetry && !progress.isActive && (
          <div style={{
            padding: 8,
            background: '#fff7e6',
            borderRadius: 4,
            border: '1px solid #ffd591'
          }}>
            <Text style={{ fontSize: 12, color: '#d46b08' }}>
              💡 You can retry only the failed line items without affecting the successfully saved ones.
            </Text>
          </div>
        )}
      </Space>
    </Modal>
  );
};

// Default props for backward compatibility
CampaignSaveProgress.defaultProps = {
  onPause: null,
  onResume: null,
  campaignOrderId: null,
  parentCampaignId: null,
};